import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  App as AntdApp,
  Typography,
  Space,
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  EyeOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  ApartmentOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../store';
import { loginUser } from '../store/slices/authSlice';
import { LoginForm } from '../types';

const { Title, Text, Link } = Typography;

const featureItems = [
  {
    icon: <ThunderboltOutlined />,
    title: '高效运营协同',
    description: '统一处理用户、服务、订单与派单流程，提升后台协作效率。',
  },
  {
    icon: <DatabaseOutlined />,
    title: '核心数据总览',
    description: '集中查看业务数据、服务状态和经营指标，辅助快速决策。',
  },
  {
    icon: <ApartmentOutlined />,
    title: '多端业务支撑',
    description: '联动用户端、服务人员端与后台管理端，形成完整业务闭环。',
  },
];

// 生成随机验证码（纯数字）
const generateCaptcha = (): string => {
  const chars = '0123456789';
  let captcha = '';
  for (let i = 0; i < 4; i++) {
    captcha += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return captcha;
};

const Login: React.FC = () => {
  const { message } = AntdApp.useApp();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector((state) => state.auth);
  const [form] = Form.useForm();
  const [captcha, setCaptcha] = useState<string>('');

  const refreshCaptcha = useCallback(() => {
    setCaptcha(generateCaptcha());
    form.setFieldValue('captcha', '');
  }, [form]);

  useEffect(() => {
    refreshCaptcha();
  }, [refreshCaptcha]);

  const handleSubmit = async (values: LoginForm) => {
    const inputCaptcha = values.captcha?.trim();
    if (inputCaptcha !== captcha) {
      message.error('验证码错误，请重新输入');
      refreshCaptcha();
      return;
    }

    try {
      await dispatch(loginUser(values)).unwrap();
      message.success('登录成功！');
      navigate('/dashboard');
    } catch (submitError) {
      message.error('登录失败，请检查用户名和密码');
    }
  };

  const handleForgotPassword = () => {
    message.info('请联系管理员重置密码');
  };

  return (
    <div className="login-container">
      <div className="login-background-grid" />
      <div className="login-background-glow login-background-glow-primary" />
      <div className="login-background-glow login-background-glow-secondary" />

      <div className="login-shell">
        <section className="login-hero-panel">
          <div className="login-hero-badge">
            <span className="login-hero-badge-dot" />
            智能运营管理后台
          </div>

          <div className="login-hero-content">
            <div className="login-hero-logo">
              <EyeOutlined />
            </div>
            <Title level={1} className="login-hero-title">
              悠米宝贝宠物管理一体化系统
            </Title>
            <Text className="login-hero-description">
              统一连接后台管理、服务执行与用户业务流程，打造更高效、更稳定的数字化运营平台。
            </Text>
          </div>

          <div className="login-feature-list">
            {featureItems.map((item) => (
              <div key={item.title} className="login-feature-item">
                <div className="login-feature-icon">{item.icon}</div>
                <div className="login-feature-text">
                  <div className="login-feature-title">{item.title}</div>
                  <div className="login-feature-description">{item.description}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="login-form-panel">
          <Card variant="borderless" className="login-form-card">
            <div className="login-form-header">
              <Space orientation="vertical" size={10} style={{ width: '100%' }}>
                <div className="login-form-logo">
                  <EyeOutlined />
                </div>
                <Title level={3} className="login-form-title">
                  欢迎登录!
                </Title>
                <Text className="login-form-subtitle">
                  请输入账号信息，进入管理控制台。
                </Text>
              </Space>
            </div>

            <Form
              form={form}
              name="login"
              onFinish={handleSubmit}
              size="large"
              layout="vertical"
              autoComplete="off"
              className="login-panel-form"
            >
              <Form.Item
                name="username"
                label="用户名"
                rules={[
                  { required: true, message: '请输入用户名' },
                  { min: 3, message: '用户名至少3个字符' },
                ]}
              >
                <Input
                  id="login_username"
                  prefix={<UserOutlined />}
                  placeholder="请输入用户名"
                  autoComplete="username"
                  className="login-panel-input"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="密码"
                rules={[
                  { required: true, message: '请输入密码' },
                  { min: 6, message: '密码至少6个字符' },
                ]}
              >
                <Input.Password
                  id="login_password"
                  prefix={<LockOutlined />}
                  placeholder="请输入密码"
                  autoComplete="current-password"
                  className="login-panel-input"
                />
              </Form.Item>

              <Form.Item
                name="captcha"
                label="验证码"
                htmlFor="login_captcha_input"
                rules={[
                  { required: true, message: '请输入验证码' },
                ]}
              >
                <Space.Compact className="login-captcha-group">
                  <Input
                    id="login_captcha_input"
                    prefix={<SafetyCertificateOutlined />}
                    placeholder="请输入验证码"
                    autoComplete="off"
                    maxLength={4}
                    className="login-panel-input"
                  />
                  <Button
                    type="default"
                    onClick={refreshCaptcha}
                    icon={<ReloadOutlined />}
                    className="login-captcha-button"
                  >
                    {captcha}
                  </Button>
                </Space.Compact>
              </Form.Item>

              {error && <div className="login-error-banner">{error}</div>}

              <Form.Item style={{ marginBottom: 18 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  className="login-submit-button"
                >
                  登录系统
                </Button>
              </Form.Item>
            </Form>

            <div className="login-form-footer">
              <Text className="login-form-footer-text">如需重置账号或开通权限，请联系管理员。</Text>
              <Link onClick={handleForgotPassword}>忘记密码？</Link>
            </div>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default Login;
